<template>
  <div class="success-page">
    <circle-stage>
      <template slot="headline">
        {{ $t('Success.headline') }}
      </template>

      <ol class="steps" v-if="deviceType">
        <li>
          {{ $t(`Success.${deviceType}.steps.searchIcon`) }}
          <component :is="`${deviceType}-icon`" class="icon" />
        </li>
        <li>
          {{ $t(`Success.${deviceType}.steps.select`) }}
          <code>
            <span v-if="device && device.title">
              {{ device.title }}
            </span>
            <span v-else>
              {{ $t(`Success.${deviceType}.deviceName`) }}
            </span>
          </code>
        </li>
      </ol>
      <p v-else>
        {{ $t('Success.noDeviceType') }}
      </p>
    </circle-stage>

    <div class="apps" v-if="deviceType">
      <div class="content">
        {{ $t(`Success.${deviceType}.appsHeadline`) }}

        <ul>
          <li v-for="app in apps" :key="app.name">
            <a
              :href="getCorrectPlatformLink(app.link)"
              :aria-label="$t('Success.appLabel', { app: app.name })"
              target="_blank"
            >
              <component :is="app.icon" class="icon" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import CircleStage from '@/components/CircleStage/CircleStage.vue';
import AppleTvIcon from '@/components/icons/AppleTvIcon.vue';
import ChromecastIcon from '@/components/icons/ChromecastIcon.vue';

import NetflixIcon from '@/components/icons/NetflixIcon.vue';
import AmazonPrimeIcon from '@/components/icons/AmazonPrimeIcon.vue';
import DisneyPlusIcon from '@/components/icons/DisneyPlusIcon.vue';

export default {
  name: 'Success',
  components: {
    CircleStage,
    'fallback-icon': ChromecastIcon,
    'chromecast-icon': ChromecastIcon,
    'apple_tv-icon': AppleTvIcon,
    NetflixIcon,
    AmazonPrimeIcon,
    DisneyPlusIcon,
  },
  computed: {
    ...mapGetters({
      apps: 'streamcast/apps',
      device: 'streamcast/device',
      deviceType: 'streamcast/deviceType',
    }),
  },
  methods: {
    getCorrectPlatformLink({ web, android, ios }) {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;

      if (/windows phone/i.test(userAgent)) {
        return web;
      }

      if (/android/i.test(userAgent)) {
        return android;
      }

      if (/iPhone|iPad|iPod/i.test(userAgent) && !window.MSStream) {
        return ios;
      }

      return web;
    },
  },
};
</script>

<style lang="scss" scoped>
.success-page {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ol.steps {
    display: inline-block;
    padding-left: 0;
    text-align: center;
    list-style: inside;
    list-style-type: decimal;

    li {
      margin-bottom: calc(2 * var(--spacing-side));

      &:last-child {
        margin-bottom: 0;
      }

      .icon {
        display: block;
        text-align: center;
        max-height: 80px;
        margin: 0 auto;
        margin-top: var(--spacing-side);
      }

      code {
        display: block;
        margin-top:  calc(2 * var(--spacing-side));

        span {
          padding: 15px 60px;
          font-family: var(--font-regular-family);
          font-weight: bold;
          text-align: center;
          font-size: 1.2em;
          color: var(--colors-attention);
          border: 2px solid var(--colors-attention);
          border-radius: 4px;
        }
      }
    }
  }

  .apps {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    padding: var(--spacing-side);

    .content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      max-width: var(--spacing-width-max);
      color: var(--colors-secondary-font);
      text-align: center;
      font-size: .9em;

      ul {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 20px;
        padding: 0;

        li {
          display: flex;

          a {
            display: flex;
            justify-content: center;
            width: 110px;
            height: 60px;
            padding: 8px 4px;
            border-radius: 4px;
            background-color: var(--colors-supportedApps-background);
            box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 1);
            box-sizing: border-box;
            border: 0px;

            @media (prefers-color-scheme: light) {
              box-shadow: 0 10px 20px 0 rgba(0, 0, 0, .08);
            }

            .icon {
              width: 100%;
              height: auto;
              max-height: 100%;
            }
          }
        }
      }
    }
  }
}
</style>
